import { createSelector } from 'reselect'
import type { State } from 'types/redux'
import type { Comment, FullWriterSplit, NativeTag, Track } from 'types/track'
import {
  getTrack,
  getTracks,
  getTracksByIds,
} from '@disco/state-management/selectors/tracks'
/**
 * Gets the copy metadata state.
 * @param state - The Redux state
 * @returns The copy metadata state object
 */
export const selectCopyMetadataState = (state: State): State['copyMetadata'] =>
  state.copyMetadata
/**
 * Gets the source track for metadata copying.
 * @param state - The Redux state
 * @returns The source track object, or null if no source track is selected
 */
export const selectCopyMetadataSourceTrack = (
  state: State,
): Track | null | undefined => {
  const { sourceTrackId } = selectCopyMetadataState(state)
  return sourceTrackId
    ? getTrack(state, {
        id: sourceTrackId,
      })
    : null
}
/**
 * Gets the target tracks for metadata copying.
 * @param state - The Redux state
 * @returns Array of target track objects
 */
export const selectCopyMetadataTargetTracks = (state: State): Track[] => {
  const { targetTrackIds } = selectCopyMetadataState(state)
  return targetTrackIds
    ? getTracksByIds(state, {
        ids: targetTrackIds,
      })
    : []
}
/**
 * Gets the selected source fields for metadata copying based on field selection.
 * @param state - The Redux state
 * @param fields - Object containing field selection configuration
 * @returns Object with selected fields from the source track
 */
export const selectCopyMetadataSourceFields = createSelector(
  [selectCopyMetadataSourceTrack, (_state, fields) => fields],
  (track, fields) => {
    const { artwork, metadata_keys, native_tags_indexes } = fields
    if (!track) return {}

    const metadata = {}

    for (const key in metadata_keys) {
      if (metadata_keys[key]) {
        metadata[key] = track.metadata[key] ?? null
      }
    }

    const native_tags: NativeTag[] = []

    for (const key in native_tags_indexes) {
      const tag = native_tags_indexes[key] && track.native_tags?.[Number(key)]
      if (tag) native_tags.push(tag)
    }

    return {
      artwork: artwork ? track.artwork : undefined,
      metadata,
      native_tags,
    }
  },
)
/**
 * Calculates the number of changes that would be made when copying metadata to target tracks.
 * @param state - The Redux state
 * @param fields - Object containing field selection configuration
 * @returns Object mapping track IDs to the number of changes that would be made
 */
export const selectCopyMetadataChanges = createSelector(
  [selectCopyMetadataState, getTracks, (_state, fields) => fields],
  (state, tracks, fields) => {
    const { sourceTrackId, targetTrackIds } = state

    if (
      !fields.metadata_keys ||
      !sourceTrackId ||
      targetTrackIds.length === 0
    ) {
      return {}
    }

    const sourceTrack: Track = tracks[sourceTrackId]
    return targetTrackIds.reduce((accumulator, trackId) => {
      const result = accumulator
      let count = 0
      const metadataChanges = Object.keys(fields.metadata_keys)
        .map((key: string) => {
          if (
            fields.metadata_keys[key] &&
            tracks[trackId].metadata[key] !== sourceTrack.metadata[key]
          ) {
            return true
          }
          return false
        })
        .filter(Boolean)
      count = metadataChanges.length

      if (fields.artwork) {
        count = count + 1
      }

      // bulkSaveRightsSplits replaces the target's split chains, only boolean check required
      const hasSelectedRightsSplits = Object.values(
        fields.rights_split_ids ?? {},
      ).some(Boolean)
      if (hasSelectedRightsSplits) {
        count = count + 1
      }

      if (count > 0) {
        result[trackId] = count
      }

      return result
    }, {})
  },
)
